import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WizardStepper from "@/components/wizard/WizardStepper";
import TutorialDeepLink from "@/components/TutorialDeepLink";
import ProfileForm from "@/components/wizard/ProfileForm";
import {
  getProject,
  listProfileTemplates,
  listProjectProfiles,
} from "@/lib/profiles";

const MAX_PROFILES = 3;

export default function WizardProfiles() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [profiles, setProfiles] = useState([]); // server persisted
  const [drafts, setDrafts] = useState([]); // unsaved tentative slots: [{id: null}]
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [proj, tmpls, profs] = await Promise.all([
        getProject(projectId),
        listProfileTemplates(),
        listProjectProfiles(projectId),
      ]);
      if (!proj) {
        toast.error("Projet introuvable.");
        navigate("/app/dashboard", { replace: true });
        return;
      }
      setProject(proj);
      setTemplates(tmpls);
      setProfiles(profs);
      // If no profiles yet, start with one empty draft
      setDrafts((prev) => {
        if (profs.length === 0 && prev.length === 0) {
          return [{ localId: "d0" }];
        }
        return prev;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger le projet.");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const totalSlots = profiles.length + drafts.length;

  const onSaved = (savedDraftLocalId, fresh) => {
    setProfiles((prev) => {
      const found = prev.find((p) => p.id === fresh.id);
      if (found) return prev.map((p) => (p.id === fresh.id ? fresh : p));
      return [...prev, fresh];
    });
    // Remove the specific draft slot that was saved (not just the first one).
    if (savedDraftLocalId) {
      setDrafts((prev) => prev.filter((d) => d.localId !== savedDraftLocalId));
    }
  };

  const onRemoved = (localId, removedId) => {
    if (removedId) {
      setProfiles((prev) => prev.filter((p) => p.id !== removedId));
    } else if (localId) {
      setDrafts((prev) => prev.filter((d) => d.localId !== localId));
    }
  };

  const addDraft = () => {
    if (totalSlots >= MAX_PROFILES) {
      toast.error(`Maximum ${MAX_PROFILES} profils comparatifs.`);
      return;
    }
    setDrafts((prev) => [...prev, { localId: `d${prev.length}-${Date.now()}` }]);
  };

  const canContinue = profiles.length >= 1;

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid="wizard-profiles-loading">
        <div className="text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="wizard-profiles-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Nouveau projet · Étape 2/7
        </div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">
          Profils contribuables
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Configurez jusqu'à <strong>3 profils comparatifs</strong>. Chaque profil
          sera converti en <em>tranches de vies</em> selon la formule
          vitaliste&nbsp;: <span className="font-mono">J = P / (S/30)</span>.
        </p>
        {project && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="wizard-project-name">
            Projet&nbsp;: <strong className="text-foreground">{project.name}</strong>
            {project.country && (
              <>
                {" · "}
                {project.country}
              </>
            )}
            {project.fiscal_year && (
              <>
                {" · Exercice "}
                {project.fiscal_year}
              </>
            )}
          </p>
        )}
      </div>

      <WizardStepper current={2} />
      <TutorialDeepLink
        chapter={3}
        section="section-3-1"
        id="wizard-step-2-profile"
        label="Pourquoi un profil contribuable est-il indispensable ?"
        duration={6}
      />

      {/* Persisted profiles */}
      <div className="space-y-4" data-testid="profiles-list">
        {profiles.map((p, i) => (
          <ProfileForm
            key={p.id}
            index={i}
            templates={templates}
            value={p}
            projectId={projectId}
            onSaved={(fresh) => onSaved(null, fresh)}
            onRemoved={() => onRemoved(null, p.id)}
          />
        ))}

        {drafts.map((d, idx) => (
          <ProfileForm
            key={d.localId}
            index={profiles.length + idx}
            templates={templates}
            value={null}
            projectId={projectId}
            onSaved={(fresh) => onSaved(d.localId, fresh)}
            onRemoved={() => onRemoved(d.localId, null)}
          />
        ))}
      </div>

      {/* Add profile button */}
      {totalSlots < MAX_PROFILES && (
        <div>
          <Button
            variant="outline"
            onClick={addDraft}
            data-testid="add-profile-btn"
            className="w-full border-dashed"
          >
            <Plus className="mr-2 h-4 w-4" />
            Ajouter un profil ({totalSlots}/{MAX_PROFILES})
          </Button>
        </div>
      )}

      {/* Empty state */}
      {profiles.length === 0 && drafts.length === 0 && (
        <Card data-testid="profiles-empty" className="border-dashed">
          <CardHeader>
            <CardTitle className="text-base">Aucun profil configuré</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-sm text-muted-foreground">
              Commencez par ajouter un profil contribuable. Vous pourrez choisir
              un profil prédéfini ou saisir vos propres valeurs.
            </p>
            <Button onClick={addDraft} data-testid="profiles-add-first">
              <Plus className="mr-2 h-4 w-4" />
              Ajouter un premier profil
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
        <Button
          variant="outline"
          onClick={() => navigate("/app/new-project")}
          data-testid="wizard-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Étape précédente
        </Button>
        <p className="text-xs text-muted-foreground">
          {profiles.length} profil{profiles.length > 1 ? "s" : ""} enregistré
          {profiles.length > 1 ? "s" : ""} · Minimum 1 pour continuer
        </p>
        <Button
          onClick={() =>
            navigate(`/app/projects/${projectId}/wizard/confirm`)
          }
          disabled={!canContinue}
          data-testid="wizard-continue-btn"
        >
          Valider et continuer
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
